import Promise from 'bluebird'
import _ from 'lodash'

import { Formatter } from '../helpers/formatter'
import { JSONSchemaResolver } from '../resolvers/json-schema-resolver'
import { Query } from './query'

const ENTITY_SCHEMA_ID = '/1.0/entities/metadata/entity.json'
const EDGE_REF = '/1.0/entities/metadata/entity.json#/definitions/edge'

const GROUP_BY_ACTIVE_MIXINS_QUERY = {
  groups: [
    {
      path: 'mixins.active',
      type: 'edge',
    },
  ],
}

const DEFAULT_COLUMN_TEMPLATE = {
  isSortable: true,
  isVisible: true,
  width: 120,
}

const GROUP_OPTIONS_TEMPLATES = {}
const FILTER_OPTIONS_TEMPLATES = {}
const TABLE_COLUMN_TEMPLATE = {}

const registerColumnType = (type: string, value: any) => {
  TABLE_COLUMN_TEMPLATE[type] = value
}

const registerFilterType = (type: string, value: any) => {
  FILTER_OPTIONS_TEMPLATES[type] = value
}

const registerGroupType = (type: string, value: any) => {
  GROUP_OPTIONS_TEMPLATES[type] = value
}

///////////////////////////////////////////////////////////////////////////////
// Columns
///////////////////////////////////////////////////////////////////////////////
registerColumnType('string', DEFAULT_COLUMN_TEMPLATE)
registerColumnType('number', DEFAULT_COLUMN_TEMPLATE)
registerColumnType('integer', DEFAULT_COLUMN_TEMPLATE)
registerColumnType('boolean', { ...DEFAULT_COLUMN_TEMPLATE, format: { type: 'boolean' } })
registerColumnType('/1.0/entities/metadata/entity.json#/definitions/edge', DEFAULT_COLUMN_TEMPLATE)
registerColumnType('/1.0/entities/metadata/entity.json#/definitions/fileSet', (path) => ({
  ...DEFAULT_COLUMN_TEMPLATE,
  cellComponent: {
    type: 'ui:table:filesCell',
    valuePath: path
  }
}))
registerColumnType('/1.0/entities/metadata/dispatchOrder.json#/definitions/trailerType', DEFAULT_COLUMN_TEMPLATE)
registerColumnType('/1.0/entities/metadata/entity.json#/definitions/time', { ...DEFAULT_COLUMN_TEMPLATE, width: 130 })

///////////////////////////////////////////////////////////////////////////////
// Filters
///////////////////////////////////////////////////////////////////////////////
registerFilterType('string', { type: 'match' })
registerFilterType('string:date', { type: 'range' })
registerFilterType('string:date-time', { type: 'range' })
registerFilterType('string:email', { type: 'match' })
registerFilterType('number', { type: 'match' })
registerFilterType('integer', { type: 'match' })
registerFilterType('boolean', { type: 'match' })
registerFilterType('array:string', { type: 'contains' })
registerFilterType('array:number', { type: 'contains' })
registerFilterType('array:integer', { type: 'contains' })
registerFilterType('array:boolean', { type: 'contains' })
registerFilterType('edge', { type: 'matchEdge' })
registerFilterType('array:edge', { type: 'containsEdge' })

///////////////////////////////////////////////////////////////////////////////
// Groups
///////////////////////////////////////////////////////////////////////////////
registerGroupType('string', { type: 'field' })
registerGroupType('/1.0/entities/metadata/entity.json#/definitions/edge', { type: 'edge' })

export class QueryOptionsGenerator {

  public static getValidSchemas(apis, entitySchema) {
    const store = apis.getStore()
    const collection = new Query(apis)
      .setEntityType(entitySchema.id)
      .setGroups(GROUP_BY_ACTIVE_MIXINS_QUERY.groups)
      .setShouldIncludeLeafEntities(false)
      .setDebug({ context: 'model--FilterQueryOptions' })
      .getCollection()
    return new Promise((resolve, reject, onCancel) => {
      onCancel(() => collection.cancel())
      collection.find().then((content: any[]) => {
        // sort derived entity schemas by displayName
        const schemaIds = _.map(content, (option: any) => option.data.entityId)
        return store.findRecords(schemaIds)
      }).then((validSchemas) => {
        const entitySchema = store.getRecord(ENTITY_SCHEMA_ID)
        validSchemas.push(entitySchema)
        _.sortBy(validSchemas, 'displayName')
        resolve(validSchemas)
      })
    }).finally(() => {
      collection.dispose()
    })
  }

  public static getFiltersFromGroups(groupSpecs, groups) {
    return _.map(groups, (group: any, index) => {
      const { label, path, type } = groupSpecs[index]
      const value = group.data
      if (type === 'edge') {
        return { label, path, type: 'matchEdge', value }
      } else if (type === 'lane') {
        return { label, path, type: 'matchLane', value }
      }
      return { label, path, type: 'match', value }
    })
  }

  public static getColumnOptions(columns, validSchemas) {
    const options = []
    // We want to ignore this column in the option generator
    // https://app.asana.com/0/389978771254907/512436922383045
    const optionFilter = (propertySchema: unknown, path: string) => {
      return _.get(propertySchema, 'isColumnAvailable', true) &&
          !_.find(columns, { path }) && !_.find(options, { path })
    }
    _.forEach(validSchemas, (entitySchema) => {
      const entitySchemaOptions = this.getValidEntityOptions(
        entitySchema, TABLE_COLUMN_TEMPLATE, optionFilter)
      options.push(...entitySchemaOptions)
    })
    return options
  }

  public static getFilterOptions(validSchemas) {
    const options = []
    const optionFilter = (propertySchema, path) => {
      return propertySchema.isFilterable && !_.find(options, { path })
    }
    _.forEach(validSchemas, (entitySchema) => {
      const entitySchemaOptions = this.getValidEntityOptions(
        entitySchema, FILTER_OPTIONS_TEMPLATES, optionFilter, validSchemas)
      options.push(...entitySchemaOptions)
    })
    return options
  }

  public static getGroupOptions(groups, validSchemas) {
    const options = []
    const optionFilter = (propertySchema, path) => {
      return !_.find(groups, { path }) && !_.find(options, { path })
    }
    _.forEach(validSchemas, (entitySchema) => {
      const entitySchemaOptions = this.getValidEntityOptions(
        entitySchema, GROUP_OPTIONS_TEMPLATES, optionFilter)
      options.push(...entitySchemaOptions)
    })
    return options
  }

  private static getValidEntityOptions(entitySchema, optionTemplates, isValidOption, referenceSchemas?: any[]) {
    const namespace = entitySchema.id === ENTITY_SCHEMA_ID ? null :
        entitySchema.get('metadata.namespace')
    const properties = entitySchema.id === ENTITY_SCHEMA_ID ? entitySchema.properties :
        entitySchema.get(`properties.${namespace}.properties`)
    const pathParts = namespace ? [namespace] : []
    return [
      ...this.getValidOptions(entitySchema, optionTemplates, isValidOption, namespace, properties, pathParts, 0, [], false, referenceSchemas),
      ...this.getDenormalizedPropertyOptions(entitySchema, optionTemplates, isValidOption, properties, pathParts),
    ]
  }

  /**
   * Separate traversal pass that finds object-format denormalizedProperties entries
   * (i.e. `{ path, isIndexable, isFilterable }`) on edge properties and emits options
   * for them. String-format entries (old copy system) are intentionally ignored.
   *
   * Recurses into nested `properties` and `items.properties` so that edges inside
   * nested objects or arrays (e.g. appointment.freightDetails[].someEdge) are found.
   */
  private static getDenormalizedPropertyOptions(entitySchema, optionTemplates, isValidOption, schema, pathParts: string[]) {
    const options = []
    _.forEach(schema, (propertySchema, key) => {
      const currentPath = [...pathParts, key]

      // Check this property for object-format denormalizedProperties entries
      const denormProps = propertySchema.denormalizedProperties
      if (Array.isArray(denormProps)) {
        for (const denormProp of denormProps) {
          if (typeof denormProp !== 'object' || denormProp === null) {
            continue
          }
          if (!denormProp.isIndexable && !denormProp.isFilterable) {
            continue
          }
          const denormPath: string = denormProp.path
          if (!denormPath) {
            continue
          }
          // Build the ES dotted path: edge.denormalizedProperties.field.subfield
          const path = [...currentPath, 'denormalizedProperties', ...denormPath.split('.')].join('.')
          const lastSegment = denormPath.split('.').pop() ?? denormPath
          const syntheticSchema = {
            type: 'string',
            label: denormProp.label ?? `${propertySchema.label || Formatter.camelCaseToWord(key)} ${Formatter.camelCaseToWord(lastSegment)}`,
            isFilterable: !!denormProp.isFilterable,
          }
          const optionTemplate = this.getOptionTemplate(optionTemplates, path, syntheticSchema)
          if (!optionTemplate) {
            continue
          }
          if (isValidOption(syntheticSchema, path)) {
            options.push({
              ...optionTemplate,
              entityType: entitySchema.id,
              label: syntheticSchema.label,
              path,
            })
          }
        }
      }

      // Recurse into nested object properties
      if (propertySchema.properties) {
        const nested = this.getDenormalizedPropertyOptions(
          entitySchema, optionTemplates, isValidOption, propertySchema.properties, currentPath)
        options.push(...nested)
      }

      // Recurse into array item properties (e.g. appointment.freightDetails[].someEdge)
      if (propertySchema.items?.properties) {
        const nested = this.getDenormalizedPropertyOptions(
          entitySchema, optionTemplates, isValidOption, propertySchema.items.properties, currentPath)
        options.push(...nested)
      }
    })
    return options
  }

  private static resolveReference(schema, curSchema: any, referenceSchemas: any[] | undefined) {
    const ref = schema?.[JSONSchemaResolver.REF_KEY]
    if (!ref) {
      return
    }

    const [refSchemaId, refPath] = ref.split('#')
    const refSchema = _.isEmpty(refSchemaId) ? curSchema : referenceSchemas?.find((s: any) => s.id === refSchemaId)
    if (!refSchema) {
      return
    }

    const refDotPath = refPath.replaceAll('/', '.').slice(1) // e.g. /definitions/labeledPhoneNumber => definitions.labeledPhoneNumber
    return _.get(refSchema, refDotPath)
  }

  private static getValidOptions(entitySchema, optionTemplates, isValidOption, namespace, schema, pathParts, depth, labelParts?: string[] | undefined, isWithinArray?: boolean | undefined, referenceSchemas?: any[] | undefined) {
    const options: any[] = []
    const refSchema = this.resolveReference(schema, entitySchema, referenceSchemas)

    if (depth > 50) {
      return options
    }

    if (refSchema?.properties) {
      schema = { ...schema, ...refSchema.properties }
    } else if (refSchema) {
      schema = { ...schema, ...refSchema }
    } else if (schema?.properties) {
      schema = schema.properties
    }

    _.forEach(schema, (propertySchema, key) => {
      const pathArr = _.clone(pathParts)
      const path = [...pathArr, key].join('.')
      const properties = propertySchema.properties

      const refSchema = this.resolveReference(propertySchema, entitySchema, referenceSchemas)
      if (refSchema) {
        propertySchema = { ...refSchema, ...propertySchema }
      }

      if (properties) {
        // recurse over subschema that have 'properties'
        pathArr.push(key)
        const nestedOptions = this.getValidOptions(entitySchema, optionTemplates, isValidOption,
          namespace, properties, pathArr, depth + 1, labelParts, isWithinArray, referenceSchemas)
        options.push(...nestedOptions)
      } else if (propertySchema.type === 'array') {
        if (propertySchema.items?.type && isValidOption(propertySchema, path)) {
          const optionTemplate = this.getOptionTemplate(optionTemplates, path, propertySchema.items, true)
          if (optionTemplate) {
            const entityType = entitySchema.id
            const label = propertySchema.label || Formatter.camelCaseToWord(key)
            options.push({ ...optionTemplate, entityType, label, path })
          }
        } else if (propertySchema.items?.type === 'object' || propertySchema.items?.[JSONSchemaResolver.REF_KEY]) {
          pathArr.push(key)
          // pathArr.push('*')
          const label = propertySchema.label || Formatter.camelCaseToWord(key)
          options.push(...this.getValidOptions(entitySchema, optionTemplates, isValidOption,
            namespace, propertySchema.items, pathArr, depth + 1, (labelParts ?? []).concat(label), true, referenceSchemas))
        }
      } else {
        const optionTemplate = this.getOptionTemplate(optionTemplates, path, propertySchema, isWithinArray)
        if (!optionTemplate) { return }
        if (isValidOption(propertySchema, path)) {
          const entityType = entitySchema.id

          let label
          if (propertySchema.priorityLabel) {
            label = propertySchema.priorityLabel
          } else {
            const labelPart = propertySchema.label || Formatter.camelCaseToWord(key)
            label = (labelParts ?? []).concat(labelPart).filter((l: any) => !_.isNil(l)).join(' - ')
          }

          options.push({ ...optionTemplate, entityType, label, path })
        }
      }
    })

    return options
  }

  private static getOptionTemplate(optionTemplates, path: string, schema: any, isWithinArray?: boolean) {
    let key = schema.type
    if (schema[JSONSchemaResolver.REF_KEY] === EDGE_REF) {
      key = 'edge'
    }

    if (schema.type === 'string' && !_.isNil(schema.format)) {
      key = `${schema.type}:${schema.format}`
    }

    if (isWithinArray) {
      key = `array:${key}`
    }

    const optionTemplate = optionTemplates[key]
    if (!optionTemplate) { return }
    if (_.isFunction(optionTemplate)) {
      return optionTemplate(path, schema)
    }
    return optionTemplate
  }
}
